import React, {useCallback, useContext, useMemo} from 'react';
import {useSelector} from 'react-redux';

import {MessageContext} from 'src/contexts/message-context';
import {ACCESS_RIGHTS, useAdminAccessRight} from 'src/hooks/access-rights';
import {useEventTracking} from 'src/hooks/useEventTracking';
import {selectUserDetails} from 'src/modules/profile/user/services/user-page-slice';

import Button from '@pb/kit-component-button';
import {Dialog} from '@pb/kit-component-dialog-v2';
import {Icon, Icons} from '@pb/kit-component-icon';
import {Tooltip} from '@pb/kit-component-popups';
import {useShown} from '@pb/kit-hooks-use-shown';


import {ResetUserMfaModal} from './reset-user-mfa-modal';

type Props = {
    userId: number,
    disabled?: boolean,
};

export const ResetUserMfa = ({userId, disabled}: Props) => {
    const {authInfo} = useSelector(selectUserDetails);
    const {setSuccessMessage} = useContext(MessageContext);
    const {loginActionSubmit} = useEventTracking();

    const {hasAccess} = useAdminAccessRight();

    const {
        shown: shownConfirmDialog,
        onClose,
        onClick: openConfirmDialog,
    } = useShown();

    const closeConfirmDialog = useCallback(() => {
        onClose();
        loginActionSubmit({submit: false, success: true, itemAction: 'Reset MFA'});
    }, [onClose, loginActionSubmit]);

    const onSuccess = useCallback(() => {
        onClose();
        setSuccessMessage('Your changes have been saved');
        loginActionSubmit({submit: true, success: true, itemAction: 'Reset MFA'});
    }, [onClose, loginActionSubmit, setSuccessMessage]);

    const onFail = useCallback(() => {
        loginActionSubmit({submit: true, success: false, itemAction: 'Reset MFA'});
    }, [loginActionSubmit]);

    const isDisabled = useMemo(() => {
        return !authInfo?.data?.mfaEnforced || !hasAccess(ACCESS_RIGHTS.USER_MFA_MANAGEMENT) || disabled;
    }, [authInfo, hasAccess, disabled]);

    const tooltipMessage = useMemo(() => {
        if (!hasAccess(ACCESS_RIGHTS.USER_MFA_MANAGEMENT)) {
            return 'You do not have permission to perform this action.';
        } else if (disabled) {
            return 'This option is disabled for currently inactive users.';
        } else if (!authInfo?.data?.mfaEnforced) {
            return 'Please enable MFA and click Save. Once saved, the Reset MFA options will be available';
        }
        return '';
    }, [hasAccess, disabled, authInfo]);

    if (!authInfo) {
        return null;
    }

    if (authInfo.error) {
        return (
            <Tooltip
                anchor={({anchorRef, getAnchorProps}) => (
                    <Button
                        ref={anchorRef}
                        {...getAnchorProps()}
                        startIcon={<Icon type={Icons.ERROR}/>}
                        variant="Type.SECONDARY"
                        disabled={true}>
                        Reset MFA
                    </Button>
                )}>
                {authInfo.error.message}
            </Tooltip>
        );
    }

    return (
        <>
            <Tooltip
                anchor={({anchorRef, getAnchorProps}) => {
                    return (
                        <Button
                            ref={anchorRef}
                            {...getAnchorProps()}
                            disabled={isDisabled}
                            variant="Type.SECONDARY"
                            onClick={openConfirmDialog}>
                            Reset MFA
                        </Button>
                    );
                }}>
                {tooltipMessage}
            </Tooltip>
            <Dialog
                shown={shownConfirmDialog}
                setShown={closeConfirmDialog}>
                <ResetUserMfaModal
                    userId={userId}
                    onSuccess={onSuccess}
                    onFail={onFail}
                    onClose={closeConfirmDialog}
                />
            </Dialog>
        </>
    );
};
